import t from "@repo/localization/string.macro";
import { SubscriptionPlan as CanadaSubscriptionPlanPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/subscription_plan_pb";
import { UserSubscription as CanadaUserSubscriptionPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/user_subscription_pb";
import { Product as UsaProductPb } from "@repo/protobuf/gen/web/public/shopping_pb";
import {
  AutoRenew as UsaAutoRenewPb,
  SubscriptionPlan as UsaSubscriptionPlanPb,
  SubscriptionPlanInfo as UsaSubscriptionPlanInfoPb,
  UserSubscription as UsaUserSubscriptionPb,
} from "@repo/protobuf/gen/web/public/subscription_pb";
import Protobuf, { MessageFrom } from "@repo/protobuf/utils";
import Case from "@repo/utils/Case";
import Enum from "@repo/utils/Enum";
import UObject from "@repo/utils/UObject";

import CreditLine from "./lending/CreditLine.js";

type Subscription = {
  id: Subscription.Id;
  planId: Subscription.Plan.Id;

  startAt: Date;
  endAt: Date;
  terminatedAt?: Date;

  active: boolean;

  autoRenew: Subscription.AutoRenew;
};

namespace Subscription {
  export type Id = string & {};
  export const baseName = "Kikoff Credit Service";
  export const tiers = ["basic", "premium", "ultimate"] as const;
  export type Tier = (typeof tiers)[number];
  export namespace Tier {
    export const format = Object.assign(
      (() => {
        // Need to rerun translation on every call
        const map = {
          basic: () => t`Basic`,
          premium: () => t`Premium`,
          ultimate: () => t`Ultimate`,
        } satisfies Record<Tier, () => string>;

        return (tier: Tier) => map[tier]();
      })(),
      {
        plan: (() => {
          // Need to rerun translation on every call
          const map = {
            basic: () => t`Basic plan`,
            premium: () => t`Premium plan`,
            ultimate: () => t`Ultimate plan`,
          } satisfies Record<Tier, () => string>;

          return (tier: Tier) => map[tier]();
        })(),
      }
    );
  }
  export type Resolved = Omit<Subscription, "planId"> & {
    plan: Subscription.Plan;
  };

  export type Plan = {
    id: string;
    name: string;
    durationMonths: number;
    monthlyCostCents: number;
    tier: Tier;
    creditLineAgreement?: CreditLine.Agreement;
    features: Plan.Features;
    metadata?: Record<string, string>;
  };
  export namespace Plan {
    export type Id = string & {};
    export type Features = {
      rentReporting?: boolean;
      helloPrivacy?: boolean;
      autoReadinessTools?: boolean;
    };
  }

  export type AutoRenew = {
    enabled: boolean;
  };
}

export default Subscription;

export namespace SubscriptionUsa {
  export const normalize = (
    proto: MessageFrom<UsaUserSubscriptionPb>
  ): Subscription => ({
    id: proto.token,
    planId: proto.planInfo!.id,
    startAt: Protobuf.Timestamp.toDate(proto.openedAt!),
    endAt: Protobuf.Timestamp.toDate(proto.expiresAt!),
    terminatedAt: Protobuf.Timestamp.toDate(proto.terminatedAt),
    active: proto.status === UsaUserSubscriptionPb.Status.ACTIVE,
    autoRenew: proto.autoRenew
      ? AutoRenew.normalize(proto.autoRenew)
      : { enabled: false },
  });

  export namespace Plan {
    const featuresByTier: Record<
      Subscription.Tier,
      Subscription.Plan.Features
    > = {
      basic: {},
      premium: {
        rentReporting: true,
      },
      ultimate: {
        rentReporting: true,
        helloPrivacy: true,
        autoReadinessTools: true,
      },
    };
    export function normalize(
      plan: MessageFrom<UsaSubscriptionPlanInfoPb>,
      // TODO
      // `creditLineAgreement` field is only necessary during activation, this
      // should really be included in SubscriptionPlanInfo though
      product?: MessageFrom<UsaProductPb>
    ): Subscription.Plan {
      if (!UObject.hasKey(Tier.byProtoEnum, plan.subscriptionPlan))
        throw new Error(
          `Unsupported subscription plan ${
            UsaSubscriptionPlanPb[plan.subscriptionPlan]
          }, only the following are supported: ${Object.keys(
            Tier.byProtoEnum
          ).map((key) => UsaSubscriptionPlanPb[key as any])}`
        );

      const tier = Tier.byProtoEnum[plan.subscriptionPlan];

      return {
        // Subscription plan id can be represented as a product or a
        // subscription plan, the id will be a composite of the two to provide
        // both to any downstream denormalization
        id: plan.id,
        tier,
        name: Tier.nameByTier[tier],
        durationMonths: plan.durationMonths,
        monthlyCostCents: plan.priceCents / plan.durationMonths,
        creditLineAgreement:
          product &&
          (() => {
            const agreement = product.creditLineAgreement;
            return (
              agreement && {
                id: agreement.token,
                url: agreement.agreementUrl,
                defaultCreditLimitCents: agreement.defaultCreditLimitCents,
                minimumPaymentAmountCents: agreement.minimumPaymentAmountCents,
                minimumPaymentAmountPercent:
                  agreement.minimumPaymentAmountPercents,
              }
            );
          })(),
        features: featuresByTier[tier],
        metadata: product && {
          productToken: product.token,
        },
      };
    }

    export namespace Tier {
      export const byProtoEnum = {
        [UsaSubscriptionPlanPb.BASIC]: "basic",
        [UsaSubscriptionPlanPb.PREMIUM]: "premium",
        [UsaSubscriptionPlanPb.ULTIMATE]: "ultimate",
      } satisfies Partial<Record<UsaSubscriptionPlanPb, Subscription.Tier>>;
      export const nameByTier = {
        basic: "Kikoff Credit Service",
        premium: "Kikoff Credit Service Premium",
        ultimate: "Kikoff Credit Service Ultimate",
      } satisfies Record<Subscription.Tier, string>;
    }
  }

  export namespace AutoRenew {
    export const normalize = (
      proto: MessageFrom<UsaAutoRenewPb>
    ): Subscription.AutoRenew => {
      const status = Case.fromConstant(
        Enum.keyOf(UsaAutoRenewPb.Status, proto.status)
      ).toCamel();

      return {
        enabled: status === "enabled",
      };
    };
  }
}

export namespace SubscriptionCanada {
  export const normalize = (
    proto: MessageFrom<CanadaUserSubscriptionPb>
  ): Subscription => ({
    id: proto.id,
    planId: proto.plan?.id!,
    startAt: Protobuf.Timestamp.toDate(proto.startAt!),
    endAt: Protobuf.Timestamp.toDate(proto.endAt!),
    terminatedAt: Protobuf.Timestamp.toDate(proto.terminatedAt),
    active: proto.active,
    autoRenew: { enabled: proto.autorenewEnabled },
  });
  export namespace Plan {
    export function normalize(
      proto: MessageFrom<CanadaSubscriptionPlanPb>
    ): Subscription.Plan {
      const tier = Tier.normalize(proto.tier);

      return {
        id: proto.id,
        tier,
        durationMonths: proto.durationMonths,
        monthlyCostCents: proto.monthlyCostCents,
        // Proper noun, no need to translate
        name: `Kikoff Credit Service - ${Subscription.Tier.format(tier)}`,
        creditLineAgreement: proto.creditLineAgreement!,
        features: {},
      };
    }

    export namespace Tier {
      export function normalize(
        proto: CanadaSubscriptionPlanPb.Tier
      ): Subscription.Tier {
        const tier = Case.fromConstant(
          Enum.keyOf(CanadaSubscriptionPlanPb.Tier, proto)
        ).toCamel();

        if (tier === "unknown")
          throw new Error("Unexpected UNKNOWN tier in subscription plan.");

        return tier;
      }
    }
  }
}
